import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { quotesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/UI/UserAvatar';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import {
  ArrowLeftIcon, TrashIcon, ArrowDownTrayIcon, EnvelopeIcon,
  DocumentTextIcon,
  BuildingOfficeIcon, UserIcon, PhoneIcon,
} from '@heroicons/react/24/outline';

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('items');

  const { data: quote, isLoading } = useQuery({ queryKey: ['quote', id], queryFn: async () => { const r = await quotesAPI.getById(id); return r.data.data; } });

  const deleteMutation = useMutation({ mutationFn: () => quotesAPI.delete(id), onSuccess: () => { toast.success('Quote deleted'); navigate('/quotes'); } });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const handleDelete = () => setDeleteConfirmOpen(true);
  const confirmDelete = () => { deleteMutation.mutate(); setDeleteConfirmOpen(false); };

  const downloadPDF = async () => {
    try {
      const response = await quotesAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `quote-${quote.quoteNumber}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  const sendEmail = async () => {
    try { await quotesAPI.sendEmail(id); toast.success('Quote email sent'); } catch { toast.error('Failed to send email'); }
  };

  const org = quote?.customerOrganization;

  if (isLoading) return (
    <div className="space-y-5">
      <div className="h-8 w-48 bg-gray-200 dark:bg-surface-700 rounded-lg animate-pulse" />
      <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 h-64 animate-pulse" />
    </div>
  );
  if (!quote) return <div className="text-center py-16"><p className="text-gray-500">Quote not found</p><Link to="/quotes" className="btn btn-primary mt-4">Back to Quotes</Link></div>;

  const tabs = [{ id: 'items', label: 'Line Items' }, { id: 'details', label: 'Details' }, { id: 'terms', label: 'Terms & Notes' }];

  return (
    <motion.div className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between">
        <Link to="/quotes" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"><ArrowLeftIcon className="w-4 h-4" />Back to Quotes</Link>
        <div className="flex items-center gap-2">
          <button onClick={downloadPDF} className="btn btn-secondary text-sm"><ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />PDF</button>
          <button onClick={sendEmail} className="btn btn-primary text-sm"><EnvelopeIcon className="w-4 h-4 mr-1.5" />Send</button>
          <button onClick={handleDelete} className="btn btn-ghost text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 p-2.5"><TrashIcon className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Header Card */}
      <div className="bg-white dark:bg-surface-800 rounded-2xl border border-gray-200 dark:border-surface-700 shadow-sm overflow-hidden">
        <div className="h-20 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-400 relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMwLTkuOTQtOC4wNi0xOC0xOC0xOCIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />
        </div>
        <div className="px-6 pb-5 -mt-8 relative z-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white shadow-lg border-4 border-white dark:border-surface-800">
              <DocumentTextIcon className="w-8 h-8" />
            </div>
            <div className="flex-1 min-w-0 sm:mb-0.5">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="inline-flex items-center px-3 py-0.5 rounded-xl bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800/30 text-xl font-bold text-blue-800 dark:text-blue-200">Quote #{quote.quoteNumber}</h1>
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                <span>{quote.title || 'Untitled'}</span>
                {org && <span>· {org.name}</span>}
              </div>
            </div>
          </div>
          {/* Stats Row */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Subtotal', value: `$${(quote.subtotal || 0).toFixed(2)}`, bg: 'bg-blue-50 dark:bg-blue-900/20', color: 'text-blue-600 dark:text-blue-400' },
              { label: 'Discount', value: `${quote.discount || 0}%`, bg: 'bg-red-50 dark:bg-red-900/20', color: 'text-red-600 dark:text-red-400' },
              { label: 'Tax', value: `$${(quote.totalTax || 0).toFixed(2)}`, bg: 'bg-amber-50 dark:bg-amber-900/20', color: 'text-amber-600 dark:text-amber-400' },
              { label: 'Total', value: `$${(quote.total || 0).toFixed(2)}`, bg: 'bg-emerald-50 dark:bg-emerald-900/20', color: 'text-emerald-600 dark:text-emerald-400' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center border border-gray-100 dark:border-surface-700`}>
                <p className="text-xs text-gray-500 dark:text-gray-400">{s.label}</p>
                <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-surface-700">
        <div className="flex gap-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'items' && (
          <motion.div key="items" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Line Items</h2>
              </div>
              <div className="p-5">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase pb-3 pr-4">Item</th>
                      <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase pb-3 px-3">Qty</th>
                      <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase pb-3 px-3">Price</th>
                      <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase pb-3 px-3">Disc</th>
                      <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase pb-3 px-3">Tax</th>
                      <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase pb-3 pl-3">Amount</th>
                    </tr></thead>
                    <tbody>{quote.items?.map((item, index) => {
                      const subtotal = item.quantity * item.unitPrice;
                      const afterDiscount = subtotal - (subtotal * ((item.discount || 0) / 100));
                      const lineTotal = afterDiscount + (afterDiscount * ((item.tax || 0) / 100));
                      return (
                        <tr key={index} className="border-b border-gray-50 dark:border-gray-700 last:border-0">
                          <td className="py-3 pr-4"><p className="text-sm font-medium text-gray-900 dark:text-white">{item.productName || item.description}</p>{item.description && item.productName && <p className="text-xs text-gray-400 mt-0.5">{item.description}</p>}</td>
                          <td className="py-3 px-3 text-right text-sm text-gray-700 dark:text-gray-300">{item.quantity}</td>
                          <td className="py-3 px-3 text-right text-sm text-gray-700 dark:text-gray-300">${item.unitPrice?.toFixed(2)}</td>
                          <td className="py-3 px-3 text-right text-sm text-gray-700 dark:text-gray-300">{item.discount || 0}%</td>
                          <td className="py-3 px-3 text-right text-sm text-gray-700 dark:text-gray-300">{item.tax || 0}%</td>
                          <td className="py-3 pl-3 text-right text-sm font-semibold text-gray-900 dark:text-white">${lineTotal.toFixed(2)}</td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
                {/* Totals */}
                <div className="mt-5 border-t border-gray-100 dark:border-gray-700 pt-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Subtotal</span><span className="font-medium text-gray-900 dark:text-white">${(quote.subtotal || 0).toFixed(2)}</span></div>
                  {quote.discount > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Discount ({quote.discount}%)</span><span className="font-medium text-red-600">-${((quote.subtotal * quote.discount) / 100).toFixed(2)}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Tax</span><span className="font-medium text-gray-900 dark:text-white">${(quote.totalTax || 0).toFixed(2)}</span></div>
                  <div className="flex justify-between text-lg font-bold border-t border-gray-200 dark:border-gray-600 pt-3 mt-3"><span className="text-gray-900 dark:text-white">Total</span><span className="text-emerald-600">${(quote.total || 0).toFixed(2)}</span></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'details' && (
          <motion.div key="details" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              {/* Customer & Org */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
                  <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2"><UserIcon className="w-4 h-4 text-primary-500" />Contact</h2></div>
                  <div className="p-5 space-y-3">
                    {quote.customer?.name && <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-surface-700 flex items-center justify-center"><UserIcon className="w-4 h-4 text-gray-500" /></div>
                      <div><p className="text-xs text-gray-400">Name</p><p className="text-sm font-medium text-gray-900 dark:text-white">{quote.customer.name}</p></div></div>}
                    {quote.customer?.email && <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center"><EnvelopeIcon className="w-4 h-4 text-blue-600" /></div>
                      <div><p className="text-xs text-gray-400">Email</p><a href={`mailto:${quote.customer.email}`} className="text-sm font-medium text-primary-600">{quote.customer.email}</a></div></div>}
                    {quote.customer?.phone && <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center"><PhoneIcon className="w-4 h-4 text-emerald-600" /></div>
                      <div><p className="text-xs text-gray-400">Phone</p><p className="text-sm font-medium text-gray-900 dark:text-white">{quote.customer.phone}</p></div></div>}
                  </div>
                </div>
                {org && (
                  <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
                    <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2"><BuildingOfficeIcon className="w-4 h-4 text-primary-500" />Organization</h2></div>
                    <div className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-surface-700 flex items-center justify-center flex-shrink-0"><BuildingOfficeIcon className="w-5 h-5 text-gray-500" /></div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{org.name}</h3>
                          <div className="mt-2 space-y-1.5">
                            {org.industry && <p className="text-xs"><span className="text-gray-400">Industry:</span> <span className="text-gray-700 dark:text-gray-300 font-medium">{org.industry}</span></p>}
                            {org.website && <p className="text-xs"><span className="text-gray-400">Website:</span> <a href={org.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 font-medium">{org.website}</a></p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Sidebar */}
            <div className="space-y-5">
              <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm p-5 space-y-4">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</h3>
                <div className="flex items-center justify-between"><span className="text-xs text-gray-500">Valid Until</span>
                  <span className="text-xs text-gray-600 dark:text-gray-300">{quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : 'Not set'}</span></div>
                {quote.lead && <div className="flex items-center justify-between"><span className="text-xs text-gray-500">Lead</span><Link to={`/leads/${quote.lead._id || quote.lead}`} className="text-xs text-primary-600 hover:text-primary-700 font-medium">{quote.lead.firstName ? `${quote.lead.firstName} ${quote.lead.lastName}` : 'View Lead'}</Link></div>}
                {quote.deal && <div className="flex items-center justify-between"><span className="text-xs text-gray-500">Deal</span><Link to={`/deals/${quote.deal._id || quote.deal}`} className="text-xs text-primary-600 hover:text-primary-700 font-medium">{quote.deal.name || quote.deal.title || 'View Deal'}</Link></div>}
                <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-3"><span className="text-xs text-gray-500">Created By</span></div>
                  {quote.createdBy ? <div className="flex items-center gap-2.5"><UserAvatar user={quote.createdBy} size="md" /><p className="text-sm font-medium text-gray-900 dark:text-white">{quote.createdBy.firstName} {quote.createdBy.lastName}</p></div> : <p className="text-sm text-gray-400">Unknown</p>}
                </div>
                <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-2.5">
                  {[{ label: 'Created', value: quote.createdAt }, { label: 'Updated', value: quote.updatedAt }, quote.sentAt && { label: 'Sent', value: quote.sentAt }].filter(Boolean).map(item => (
                    <div key={item.label} className="flex items-center justify-between"><span className="text-xs text-gray-500">{item.label}</span><span className="text-xs text-gray-600 dark:text-gray-300">{new Date(item.value).toLocaleDateString()}</span></div>
                  ))}
                </div>
              </div>
              <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm p-5 space-y-2.5">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Quick Actions</h3>
                <button onClick={downloadPDF} className="btn btn-secondary w-full justify-center text-sm rounded-xl"><ArrowDownTrayIcon className="w-4 h-4 mr-2" />Download PDF</button>
                <button onClick={sendEmail} className="btn btn-secondary w-full justify-center text-sm rounded-xl"><EnvelopeIcon className="w-4 h-4 mr-2" />Send via Email</button>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'terms' && (
          <motion.div key="terms" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Terms & Conditions</h2></div>
              <div className="p-5"><p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{quote.terms || <span className="text-gray-400 italic">No terms added</span>}</p></div>
            </div>
            <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-surface-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Internal Notes</h2></div>
              <div className="p-5"><p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{quote.notes || <span className="text-gray-400 italic">No notes</span>}</p></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Quote"
        message={`Are you sure you want to delete quote #${quote.quoteNumber}? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </motion.div>
  );
};

export default QuoteDetail;
